// src/pages/student/StudentCircular.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Alert, InputGroup
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import {
  collection, doc, getDoc, onSnapshot, query, where, setDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../../services/firebase";

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtAt = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—";
};
const fmtDate = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";
};
const uniqBy = (arr, key = (x) => x.id) => {
  const m = new Map();
  for (const x of arr) m.set(key(x), x);
  return [...m.values()];
};
const norm = (s) => String(s || "").trim().toLowerCase();

/** Icons by category/priority */
const ICONS = {
  general: "bi-megaphone",
  exam: "bi-journal-check",
  fee: "bi-cash-coin",
  holiday: "bi-calendar2-week",
  event: "bi-calendar-event",
  sports: "bi-trophy",
  urgent: "bi-exclamation-triangle",
  default: "bi-bell",
};
const iconFor = (category, priority) => {
  if (norm(priority) === "urgent") return ICONS.urgent;
  return ICONS[norm(category)] || ICONS.default;
};

/* ---------------- local demo template ---------------- */
const makeDummyCirculars = (profile) => {
  const cls = profile?.className || "10";
  const sec = profile?.section || "A";
  const base = dayjs();
  const mk = (id, title, msg, category, priority, pubOffDays, tillOffDays, extra = {}) => ({
    id: `demo-${id}`,
    title,
    message: msg,
    category,
    priority,
    className: cls,
    sections: [sec, "all"],
    publishedAt: base.add(pubOffDays, "day").toDate(),
    validTill: tillOffDays != null ? base.add(tillOffDays, "day").toDate() : null,
    link: "https://example.com/circular.pdf",
    ...extra,
  });

  return [
    mk(1, "Unit Test Schedule Released", "Please check the exam timetable attached.", "exam", "high", -3, 10),
    mk(2, "Sports Day Practice", "Practice sessions will be held every evening.", "sports", "normal", -1, 14),
    mk(3, "Fee Payment Reminder", "Second term fee due in 5 days.", "fee", "urgent", -2, 5),
    mk(4, "Science Exhibition", "Register your projects by next Monday.", "event", "normal", 1, 15),
    mk(5, "Independence Day", "School will remain closed.", "holiday", "normal", -20, -18),
    mk(6, "General Notice", "Parent–Teacher meeting next week.", "general", "high", 2, 12),
  ];
};
const downloadJSON = (filename, data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export default function StudentCircular() {
  const brand = useBrand();
  const { primary } = brand;

  // IDs from login
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // student profile to get class/section
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // circulars
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // per-circular read status for this student
  const [reads, setReads] = useState({}); // circularId -> {readAt, ...}
  const readUnsubsRef = useRef({});

  // filters / search
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("all");
  const [fPriority, setFPriority] = useState("all"); // all|normal|high|urgent
  const [fStatus, setFStatus] = useState("all"); // all|active|expired|upcoming|unread
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // demo toggle
  const [useDummy, setUseDummy] = useState(() => localStorage.getItem("sc_useDummy") === "1");

  // particles init
  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  /* ---------- load profile ---------- */
  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.");
      return;
    }
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student profile.");
        } else {
          setProfile({ id: snap.id, ...snap.data() });
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load profile.");
      })
      .finally(() => setLoadingProfile(false));
  }, [schoolId, studentId]);

  const isActive = norm(profile?.status) !== "inactive";

  /* ---------- subscribe to circulars OR use local demo ---------- */
  useEffect(() => {
    // clear old read listeners if class/section or dummy mode changes
    Object.values(readUnsubsRef.current).forEach((u) => u && u());
    readUnsubsRef.current = {};
    setReads({});

    if (useDummy) {
      setItems(makeDummyCirculars(profile));
      setLoading(false);
      return () => {};
    }

    if (!schoolId || !profile?.className) return;

    if (!isActive) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const col = collection(db, "schools", schoolId, "circulars");

    // match by className and either section or sections[]
    const q1 = profile.section
      ? query(col, where("className", "==", profile.className), where("sections", "array-contains", profile.section))
      : null;

    const q2 = profile.section
      ? query(col, where("className", "==", profile.className), where("section", "in", [profile.section, "all"]))
      : query(col, where("className", "==", profile.className));

    const unsubs = [];

    if (q1) {
      unsubs.push(
        onSnapshot(
          q1,
          (snap) => {
            const A = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setItems((prev) => {
              const merged = uniqBy([...A, ...prev]);
              return merged.sort((a, b) => (toJS(b.publishedAt || b.createdAt) - toJS(a.publishedAt || a.createdAt)));
            });
            setLoading(false);
          },
          (err) => {
            console.error(err);
            setLoading(false);
          }
        )
      );
    }

    unsubs.push(
      onSnapshot(
        q2,
        (snap) => {
          const B = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setItems((prev) => {
            const merged = uniqBy([...prev, ...B]);
            return merged.sort((a, b) => (toJS(b.publishedAt || b.createdAt) - toJS(a.publishedAt || a.createdAt)));
          });
          setLoading(false);
        },
        (err) => {
          console.error(err);
          toast.error("Failed to load circulars.");
          setLoading(false);
        }
      )
    );

    return () => {
      unsubs.forEach((u) => u && u());
      Object.values(readUnsubsRef.current).forEach((u) => u && u());
      readUnsubsRef.current = {};
    };
  }, [schoolId, profile?.className, profile?.section, isActive, useDummy]);

  /* ---------- subscribe to student's read marker per circular (skip in demo) ---------- */
  useEffect(() => {
    if (useDummy) return;
    if (!schoolId || !studentId || items.length === 0) return;
    const active = readUnsubsRef.current;

    items.forEach((c) => {
      const ref = doc(db, "schools", schoolId, "circulars", c.id, "reads", studentId);
      if (active[c.id]) return;
      active[c.id] = onSnapshot(ref, (snap) => {
        setReads((prev) => ({ ...prev, [c.id]: snap.exists() ? { id: snap.id, ...snap.data() } : null }));
      });
    });

    const ids = new Set(items.map((x) => x.id));
    Object.keys(active).forEach((cid) => {
      if (!ids.has(cid)) {
        active[cid]?.();
        delete active[cid];
        setReads((prev) => {
          const copy = { ...prev }; delete copy[cid]; return copy;
        });
      }
    });
  }, [items, schoolId, studentId, useDummy]);

  /* ---------- computed filters ---------- */
  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((it) => {
      const cat = (it.category || "General").trim();
      if (cat) s.add(cat);
    });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const filtered = useMemo(() => {
    const now = new Date();
    return items
      .filter((c) => {
        const title = String(c.title || "").toLowerCase();
        const body = String(c.message || c.description || c.body || "").toLowerCase();
        const cat = String(c.category || "General").trim();
        const pri = String(c.priority || c.importance || "normal").toLowerCase();
        const pub = toJS(c.publishedAt || c.createdAt);
        const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
        const unread = !reads[c.id];

        // status calc
        const active = (!till || now <= till) && (!!pub ? now >= pub : true);
        const expired = !!till && now > till;
        const upcoming = !!pub && now < pub;

        if (q.trim()) {
          const needle = q.trim().toLowerCase();
          const hay = [title, body, cat.toLowerCase(), pri].join(" ");
          if (!hay.includes(needle)) return false;
        }
        if (fCategory !== "all" && cat !== fCategory) return false;
        if (fPriority !== "all" && pri !== fPriority) return false;

        if (fStatus !== "all") {
          if (fStatus === "active" && !active) return false;
          if (fStatus === "expired" && !expired) return false;
          if (fStatus === "upcoming" && !upcoming) return false;
          if (fStatus === "unread" && !unread) return false;
        }

        if (fromDate && pub && dayjs(pub).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && pub && dayjs(pub).isAfter(dayjs(toDate).endOf("day"))) return false;

        return true;
      })
      .sort((a, b) => {
        // Newest first; if same, urgent before others
        const ta = toJS(a.publishedAt || a.createdAt) || new Date(0);
        const tb = toJS(b.publishedAt || b.createdAt) || new Date(0);
        if (tb.getTime() !== ta.getTime()) return tb - ta;
        const pa = norm(a.priority || a.importance || "normal");
        const pb = norm(b.priority || b.importance || "normal");
        const w = (p) => (p === "urgent" ? 3 : p === "high" ? 2 : 1);
        return w(pb) - w(pa);
      });
  }, [items, q, fCategory, fPriority, fStatus, fromDate, toDate, reads]);

  const classTag = useMemo(
    () => [profile?.className, profile?.section].filter(Boolean).join("-") || "—",
    [profile?.className, profile?.section]
  );

  /* ---------- actions ---------- */
  const onOpen = (url) => {
    if (!url) return toast.info("File/link not available.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const markRead = async (c) => {
    if (!c?.id) return;
    // In demo mode, just set local state
    if (useDummy) {
      setReads((prev) => ({ ...prev, [c.id]: { readAt: new Date(), demo: true } }));
      toast.success("Marked as read (demo).");
      return;
    }
    if (!schoolId || !studentId) return;
    try {
      const ref = doc(db, "schools", schoolId, "circulars", c.id, "reads", studentId);
      await setDoc(ref, {
        studentId,
        className: profile?.className || "",
        section: profile?.section || "",
        readAt: serverTimestamp(),
      }, { merge: true });
      toast.success("Marked as read.");
    } catch (e) {
      console.error(e);
      toast.info("Couldn’t persist read status (permissions). You can still view the circular.", { autoClose: 3500 });
    }
  };

  /* ---------- status/priority badges ---------- */
  const badges = (c) => {
    const pri = norm(c.priority || c.importance || "normal");
    const pub = toJS(c.publishedAt || c.createdAt);
    const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
    const now = new Date();
    const active = (!till || now <= till) && (!!pub ? now >= pub : true);
    const expired = !!till && now > till;
    const upcoming = !!pub && now < pub;

    return (
      <div className="d-flex flex-wrap gap-2">
        {pri === "urgent" ? <Badge bg="danger">Urgent</Badge> :
         pri === "high" ? <Badge bg="warning" text="dark">High</Badge> :
         <Badge bg="secondary">Normal</Badge>}
        {upcoming ? <Badge bg="info" text="dark">Upcoming</Badge> : null}
        {active ? <Badge bg="success">Active</Badge> : null}
        {expired ? <Badge bg="dark">Expired</Badge> : null}
      </div>
    );
  };

  /* ---------- demo controls ---------- */
  const toggleDemo = (checked) => {
    setUseDummy(checked);
    localStorage.setItem("sc_useDummy", checked ? "1" : "0");
    if (checked) {
      setItems(makeDummyCirculars(profile));
      setReads({});
      setLoading(false);
    }
  };
  const exportDemo = () => downloadJSON("sample_circulars.json", makeDummyCirculars(profile));

  return (
    <div className="position-relative overflow-hidden">
      {/* Background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primary, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(brand.accent,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="student-circulars-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 60, density: { enable: true, area: 900 } },
            color: { value: [brand.primary, brand.accent] },
            links: { enable: true, color: hexToRgba(brand.primary, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.0, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
        }}
      />

      {/* Foreground */}
      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer
          position="bottom-right"
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          theme="dark"
        />

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Circulars & Notices</h5>
            <small className="text-muted">
              {studentName || "Student"} • Class {classTag}
              {isActive ? "" : " • Inactive"}
            </small>
          </div>

          {/* Demo toggle accessible always */}
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              type="switch"
              id="demo-switch"
              label="Sample data"
              checked={useDummy}
              onChange={(e) => toggleDemo(e.target.checked)}
            />
            <Button size="sm" variant="outline-secondary" onClick={exportDemo} title="Download sample JSON">
              <i className="bi bi-download me-1" />
              Template
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        {isActive && (
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="py-3">
              <Row className="g-2">
                <Col xs={12} md={4} lg={3}>
                  <InputGroup>
                    <InputGroup.Text className="bg-light"><i className="bi bi-search" /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search title / message / category"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>
                </Col>

                <Col xs={6} md={3} lg={2}>
                  <Form.Select value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                    {categories.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All categories" : s}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={3} lg={2}>
                  <Form.Select value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
                    <option value="all">Any priority</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={3} lg={2}>
                  <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="expired">Expired</option>
                    <option value="unread">Unread</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={2} lg={1}>
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    title="Published from"
                  />
                </Col>
                <Col xs={6} md={2} lg={1}>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    title="Published to"
                  />
                </Col>

                <Col xs={12} md={2} lg={1} className="d-grid">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setQ(""); setFCategory("all"); setFPriority("all");
                      setFStatus("all"); setFromDate(""); setToDate("");
                    }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Inactive notice */}
        {!loadingProfile && !isActive && !useDummy ? (
          <Alert variant="warning" className="mb-3">
            Your student account is currently <b>inactive</b>. Please contact your school
            management to activate your account to access circulars.
          </Alert>
        ) : null}

        {/* List / Loading */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            {loadingProfile ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : (!isActive && !useDummy) ? (
              <div className="text-muted py-4 text-center">
                Circulars are hidden until your account is activated.
              </div>
            ) : loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading circulars…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-muted py-4 text-center">
                No circulars found.{!useDummy ? " Try enabling Sample data." : ""}
              </div>
            ) : (
              <Row className="g-3">
                {filtered.map((c) => {
                  const title = c.title || "Circular";
                  const body = c.message || c.description || c.body || "";
                  const cat = c.category || "General";
                  const pri = c.priority || c.importance || "Normal";
                  const pub = toJS(c.publishedAt || c.createdAt);
                  const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
                  const isUnread = !reads[c.id];

                  const ico = iconFor(cat, pri);

                  const hasLink = !!(c.link || c.fileURL || c.attachmentUrl);
                  const tag = [c.className || "", c.section || (Array.isArray(c.sections) ? c.sections.join(",") : "")]
                    .filter(Boolean).join("-");

                  return (
                    <Col key={c.id} xs={12} md={6} lg={4}>
                      <Card
                        className={`h-100 shadow-sm border-0 circular-card ${norm(pri)}`}
                        style={{ borderColor: hexToRgba(primary, 0.22), position: "relative", overflow: "hidden" }}
                      >
                        <div className="card-particles" aria-hidden />

                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-3">
                              <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={cat}>
                                <i className={`bi ${ico}`} />
                              </div>
                              <div className="fw-semibold text-truncate" title={title}>
                                {title}
                              </div>
                            </div>
                            {isUnread ? <Badge bg="primary">New</Badge> : <Badge bg="secondary">Read</Badge>}
                          </div>

                          <div className="text-muted small">
                            Category: <span className="fw-semibold">{cat}</span> • Priority: <span className="fw-semibold">{String(pri).toUpperCase()}</span>
                          </div>

                          <div className="small mt-2 text-truncate" title={body}>{body}</div>

                          <div className="text-muted small mt-2">
                            Published: <span className="fw-semibold">{fmtAt(pub)}</span>
                            {till ? <> &nbsp;• Valid till: <span className="fw-semibold">{fmtDate(till)}</span></> : null}
                          </div>

                          <div className="mt-2">{badges(c)}</div>

                          <div className="mt-auto pt-3 d-flex justify-content-between align-items-center">
                            <Badge bg="light" text="dark" className="border">{tag || "—"}</Badge>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant={isUnread ? "success" : "outline-success"}
                                onClick={() => markRead(c)}
                              >
                                <i className="bi bi-check2-circle me-1" />
                                Mark read
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={!hasLink}
                                onClick={() => onOpen(c.link || c.fileURL || c.attachmentUrl)}
                              >
                                <i className="bi bi-box-arrow-up-right me-1" />
                                Open
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Styles */}
      <style>{`
        /* Toastify themed to accent/primary */
        .Toastify__toast {
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid ${hexToRgba(brand.accent, 0.4)};
          background: linear-gradient(
            135deg,
            ${hexToRgba(brand.primary, 0.14)} 0%,
            ${hexToRgba(brand.accent, 0.14)} 100%
          );
          backdrop-filter: blur(6px);
        }
        .Toastify__toast-body { color: ${brand.accent}; font-weight: 500; }
        .Toastify__progress-bar { background: ${brand.accent}; }

        .circular-card {
          border-radius: 18px;
          background: linear-gradient(
            180deg,
            ${hexToRgba(brand.primary, 0.06)} 0%,
            transparent 60%
          );
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .circular-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1.2rem rgba(0,0,0,.12); }
        .circular-card.urgent { box-shadow: 0 .35rem .9rem ${hexToRgba("#dc3545", .22)}; }
        .circular-card.high { box-shadow: 0 .35rem .9rem ${hexToRgba("#ffc107", .22)}; }

        .subj-ico {
          width: 58px; height: 58px; border-radius: 16px;
          background: ${hexToRgba(brand.primary, 0.18)};
          color: ${brand.primary};
          border: 1px solid ${hexToRgba(brand.primary, 0.30)};
          font-size: 30px;
        }
        @media (max-width: 576px) {
          .subj-ico { width: 50px; height: 50px; font-size: 26px; }
        }

        /* Per-card animated particle overlay (pure CSS) */
        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(${hexToRgba(brand.primary, .18)} 1px, transparent 1px),
            radial-gradient(${hexToRgba(brand.accent, .18)} 1px, transparent 1px);
          background-position: 0 0, 12px 12px;
          background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite;
          opacity: .22;
        }
        .circular-card.urgent .card-particles { animation-duration: 6.5s; opacity: .26; }
        .circular-card.high .card-particles { animation-duration: 7.5s; opacity: .24; }

        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }
      `}</style>
    </div>
  );
}
